/**
 * InviteFlowRegister — Public self-registration page for invitees.
 *
 * URL pattern: /inviteflow/:slug/register
 *
 * This page is shown to guests who follow an invitation link.
 * It is only active when:
 *   - is_foc = true  (free of charge)
 *   - requires_info = true  (guest must fill in their details)
 *
 * Flow:
 *  1. Load public link info on mount.
 *  2. Check link is still active (410 / 403 handled).
 *  3. Guest fills out name / email / phone and submits.
 *  4. On success → show confirmation message with status.
 */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import * as api from "../api";
import InviteFlowEventBanner from "../components/InviteFlow/InviteFlowEventBanner";
import { inviteflowApiErrorMessage } from "../utils/inviteflowErrors";

const CLOSED_STATUSES = ["submitted", "exhausted", "expired"];

const InviteFlowRegister = () => {
  const { slug } = useParams();

  const [linkData, setLinkData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [linkError, setLinkError] = useState("");

  const [form, setForm] = useState({
    primary_name: "",
    primary_email: "",
    primary_phone: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.inviteflow.getLink(slug);
        const data = res.data;

        if (!data.is_foc || !data.requires_info) {
          setLinkError("This invitation link does not support self-registration.");
        } else if (CLOSED_STATUSES.includes(data.link_status)) {
          setLinkError("This invitation link is no longer accepting registrations.");
        } else {
          setLinkData(data);
        }
      } catch (err) {
        const status = err?.response?.status;
        const fromApi = inviteflowApiErrorMessage(err);
        if (fromApi) {
          setLinkError(fromApi);
        } else if (status === 404) {
          setLinkError("Invitation link not found.");
        } else {
          setLinkError("Something went wrong. Please try again.");
        }
      } finally {
        setPageLoading(false);
      }
    };

    load();
  }, [slug]);

  const validate = () => {
    const errors = {};
    if (!form.primary_name.trim()) errors.primary_name = "Name is required.";
    if (!form.primary_email.trim()) {
      errors.primary_email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.primary_email)) {
      errors.primary_email = "Enter a valid email address.";
    }
    if (!form.primary_phone.trim()) errors.primary_phone = "Phone is required.";
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitLoading(true);
    setSubmitError("");

    try {
      const res = await api.inviteflow.registerGuest(slug, form);
      setRegistration(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410) {
        setSubmitError("Sorry, all spots are taken. The invitation is now closed.");
      } else {
        const detail = err?.response?.data?.detail || "Registration failed. Please try again.";
        setSubmitError(detail);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="inviteflow-register inviteflow-register--loading">
        <p>Loading invitation…</p>
      </div>
    );
  }

  if (linkError) {
    return (
      <div className="inviteflow-register inviteflow-register--error">
        <p className="inviteflow-register__error">{linkError}</p>
      </div>
    );
  }

  if (registration) {
    return (
      <div className="inviteflow-register inviteflow-register--success">
        <InviteFlowEventBanner linkData={linkData} />
        <div className="inviteflow-register__confirmation">
          <div className="inviteflow-register__check">✓</div>
          <h2>You're registered!</h2>
          <p>
            Thank you, <strong>{registration.primaryName}</strong>.
          </p>
          {registration.status === "approved" ? (
            <p>Your registration is <strong>confirmed</strong>. Check your email for details.</p>
          ) : (
            <p>Your registration is <strong>pending review</strong>. You'll hear back shortly.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="inviteflow-register">
      <InviteFlowEventBanner linkData={linkData} />

      <div className="inviteflow-register__card">
        <h2 className="inviteflow-register__title">Complete Your Registration</h2>
        <p className="inviteflow-register__subtitle">
          Please fill in your details to confirm your spot.
        </p>

        {submitError && (
          <p className="inviteflow-register__submit-error">{submitError}</p>
        )}

        <form className="inviteflow-register__form" onSubmit={handleSubmit} noValidate>
          <div className="inviteflow-register__field">
            <label htmlFor="primary_name">Full Name</label>
            <input
              id="primary_name"
              type="text"
              name="primary_name"
              value={form.primary_name}
              onChange={handleChange}
              placeholder="Your full name"
              className={formErrors.primary_name ? "inviteflow-input--error" : ""}
            />
            {formErrors.primary_name && (
              <span className="inviteflow-register__field-error">{formErrors.primary_name}</span>
            )}
          </div>

          <div className="inviteflow-register__field">
            <label htmlFor="primary_email">Email Address</label>
            <input
              id="primary_email"
              type="email"
              name="primary_email"
              value={form.primary_email}
              onChange={handleChange}
              placeholder="you@example.com"
              className={formErrors.primary_email ? "inviteflow-input--error" : ""}
            />
            {formErrors.primary_email && (
              <span className="inviteflow-register__field-error">{formErrors.primary_email}</span>
            )}
          </div>

          <div className="inviteflow-register__field">
            <label htmlFor="primary_phone">Phone Number</label>
            <input
              id="primary_phone"
              type="tel"
              name="primary_phone"
              value={form.primary_phone}
              onChange={handleChange}
              placeholder="+20 1xx xxx xxxx"
              className={formErrors.primary_phone ? "inviteflow-input--error" : ""}
            />
            {formErrors.primary_phone && (
              <span className="inviteflow-register__field-error">{formErrors.primary_phone}</span>
            )}
          </div>

          <button
            type="submit"
            className="inviteflow-btn inviteflow-btn--primary inviteflow-btn--full"
            disabled={submitLoading}
          >
            {submitLoading ? "Submitting…" : "Confirm My Registration"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default InviteFlowRegister;
